import { Header } from "./components/Header";
import { useRef } from "react";
import { handleEnterFullscreen } from "../fullScreen";
import { Card } from "./components/Card";
import { useGetAllMaintenances } from "@/common/hooks";
import { IMaintenance, MAINTENANCE_STATUS, budgetItem } from "@/types/maintenance.type";
import { formatCurrency } from "@/common/helpers/mask.helpers";
import { previousSunday, isBefore, isEqual } from 'date-fns'
import { LoadingCard } from "@/components/ui/loading-card";
import { Card as SCard, CardContent as SCardContent } from "@/components/ui/card";

export default function MaintenanceDashboard() {
  const TOP_RANKED_PRODUCTS_LIMIT = 10;
  const containerRef = useRef<HTMLDivElement>(null);
  const { data, isLoading: loading } = useGetAllMaintenances()

  const onFullScreen = () => {
    handleEnterFullscreen(containerRef.current)
  }
  const today = new Date()
  const lastSunday = previousSunday(today)


  const totalMaintenances = data?.length || 0
  const totalMaintenancesPlv = data
    ?.filter(
      maintenance => !maintenance.partnerCompanyName.includes("LOCAVIBE"))

  const totalMaintenancesLocavibe = data
    ?.filter(
      maintenance => maintenance.partnerCompanyName.includes("LOCAVIBE"))

  const budgetMaintenances = (maintenances: IMaintenance[]) => {
    if (maintenances?.length === 0 || !maintenances) return 0;

    return maintenances.reduce((acc, maintenance) => {
      const { misuse = [], corrective = [], preventive = [] } = maintenance.budgetMaintenance;
      for (const item of [...misuse, ...corrective, ...preventive]) {
        acc += item?.sellPrice || 0;
      }
      return acc;
    }, 0);
  };

  const budgetMaintenanceTotal = budgetMaintenances(data as IMaintenance[])

  const maintenancesNotCharged = totalMaintenancesPlv
    ?.filter(m => {
      const createdAt = m.createdAt
      return isBefore(createdAt, lastSunday) || isEqual(createdAt, lastSunday)
    })
    ?.filter(m => !m.chargeMaintenance?.charged) || []

  const products = data?.flatMap((maintenance) => [
    ...maintenance.budgetMaintenance.misuse,
    ...maintenance.budgetMaintenance.corrective,
    ...maintenance.budgetMaintenance.preventive,
  ]).reduce((acc: Record<string, budgetItem>, product) => {
    const { name, sellPrice, quantity } = product

    if (!acc[name]) {
      acc[name] = { name, quantity, sellPrice } as budgetItem
    } else {
      acc[name].quantity += quantity
    }

    return acc
  }, {} as Record<string, budgetItem>) || {}

  const topRankedProducts = Object.values(products)
    .sort((a: budgetItem, b: budgetItem) => b.quantity - a.quantity)
    .slice(0, TOP_RANKED_PRODUCTS_LIMIT);


  const unfinishedMaintenances = data?.map((maintenance) => maintenance.status !== MAINTENANCE_STATUS.Finalize)

  return (
    <>
      <Header onFullScreen={onFullScreen} />

      <div ref={containerRef} className="font-sans bg-white">
        <div className="xl:flex gap-6">
          <div className="xl:w-1/2 grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 p-5">
            <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <LoadingCard loading={loading}>
                <Card
                  title="Quantidade de manutenções"
                  value={formatCurrency(budgetMaintenanceTotal || 0)}
                  valueInfo={String(totalMaintenances)}
                  loading={loading}
                />
              </LoadingCard>

              <LoadingCard loading={loading}>
                <Card
                  title="Ticket médio por moto"
                  value={formatCurrency((budgetMaintenanceTotal / totalMaintenances) || 0)}
                  loading={loading}
                />
              </LoadingCard>
            </div>

            <div className="grid gap-6 md:col-span-3 w-full">
              <LoadingCard loading={loading}>
                <Card
                  key="Manutenções não finalizadas"
                  title="Manutenções não finalizadas"
                  value={String(unfinishedMaintenances?.length)}
                  delayed={Boolean((unfinishedMaintenances || []).length > 0)}
                />
              </LoadingCard>
              <LoadingCard loading={loading}>
                <Card
                  title="Manutenções não cobradas"
                  value={formatCurrency(budgetMaintenances(maintenancesNotCharged as IMaintenance[]))}
                  loading={loading}
                  delayed={maintenancesNotCharged.length > 0}
                  valueInfo={String(maintenancesNotCharged?.length || 0)}
                />
              </LoadingCard>
            </div>

            <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <LoadingCard loading={loading}>
                <Card
                  title="Total de manutenções Parceiro"
                  value={formatCurrency(budgetMaintenances(totalMaintenancesLocavibe as IMaintenance[]))}
                  loading={loading}
                  valueInfo={String(totalMaintenancesPlv?.length || 0)}
                />
              </LoadingCard>
              <LoadingCard loading={loading}>
                <Card
                  title="Total de manutenções Locavibe"
                  value={formatCurrency(budgetMaintenances(totalMaintenancesPlv as IMaintenance[]))}
                  loading={loading}
                  valueInfo={String(totalMaintenancesLocavibe?.length || 0)}
                />
              </LoadingCard>
            </div>
          </div>            

          <div className="bg-slate-100 w-1"></div> {/* division */}

          <div className="xl:w-1/2 pl-6 pr-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4
    md:bg-transparent md:rounded-none md:shadow-none md:border-0 md:p-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Top 10 Peças</h3>
              <div className="space-y-4">
                {topRankedProducts.length === 0 &&(
                  <EmptyState />
                )}
                {topRankedProducts.map((product: budgetItem) => {
                  return (
                    <div key={product.code} className="flex items-center gap-8">
                      <span className="w-6 text-sm font-medium text-gray-500 tabular-nums">{product.quantity}</span>
                      <span
                        className="w-32 sm:w-40 text-sm font-medium text-gray-700"
                        title={product.name}
                      >
                        {product.name}
                      </span>
                    <div className="flex-1">
                      <div className="bg-gray-100 rounded-full h-4 overflow-hidden">
                        <div
                          className="h-full bg-locavibe transition-all duration-300 ease-out rounded-full"
                          style={{ width: `${product?.quantity}%` }}
                          role="progressbar"
                          aria-valuenow={product.sellPrice}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label={`${product.name}: ${formatCurrency(product.sellPrice)}`}
                        />
                      </div>
                      <span className="w-24 text-right text-sm font-semibold text-gray-900 tabular-nums">
                        {formatCurrency(product.sellPrice * (product?.quantity || 1))}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  )
}

function EmptyState() {
  return (
    <SCard>
      <SCardContent className="py-12 text-center">
        <p className="text-muted-foreground">Nenhum produto na lista de TOP 10.</p>
      </SCardContent>
    </SCard>
  );
}
